/**
 * Anemoia product detail (custom).
 *
 * Drives <anemoia-product>: a Zara-style scrolling image gallery with on-scroll
 * parallax, a Nike-style size/variant selector + size-guide dialog, and an Add
 * to cart button matching the homepage CTA (Cart AJAX + CartAddEvent +
 * fly-to-cart, with the label-collapse / cart-image swell on click).
 *
 * Sold-out variants swap the CTA for "Notify me when available", which hands
 * off to the Omnisend popup form and then reports the product / variant / size
 * back to Omnisend as a custom event. See docs/anemoia-back-in-stock/README.md.
 */

import { CartAddEvent } from '@theme/events';
import '@theme/fly-to-cart';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

/**
 * Variant ids this visitor has already asked to be notified about, so the
 * button keeps its confirmation when they flick between sizes (and after a
 * reload). Per tab on purpose — a stale "you're on the list" days later would
 * be worse than asking again.
 */
const NOTIFIED_KEY = 'anemoia:back-in-stock';

function readNotified() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(NOTIFIED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function writeNotified(set) {
  try {
    sessionStorage.setItem(NOTIFIED_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // Storage blocked (private mode): the in-memory set still covers this view.
  }
}

/**
 * Pops the theme cart drawer open after an add. No-op when the store uses a cart
 * page (no drawer) or when the drawer is already open (avoids showModal throwing).
 */
function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || typeof drawer.open !== 'function') return;
  if (drawer.querySelector('dialog')?.open) return;
  drawer.open();
}

class AnemoiaProduct extends HTMLElement {
  connectedCallback() {
    this.variants = this.#parseVariants();
    this.selected = {}; // option position -> chosen value

    this.priceNow = this.querySelector('[data-apdp-price-now]');
    this.priceCompare = this.querySelector('[data-apdp-compare]');
    this.addBtn = this.querySelector('[data-apdp-add]');
    this.addLabel = this.querySelector('[data-apdp-add-label]');
    this.addImg = this.addBtn?.querySelector('.apdp__add-img');

    // Dynamic checkout ("Buy with Shop" / More payment options). Its own form,
    // so the variant the size picker chooses has to be mirrored onto it.
    this.buyForm = this.querySelector('.apdp__buyform');
    this.buyFormVariant = this.querySelector('[data-apdp-form-variant]');

    this.#initSelectedFromMarkup();
    this.setupNotify(); // before setupOptions: the first sync happens in there
    this.setupOptions();
    this.setupAddToCart();
    this.setupSizeGuide();
    this.setupGallerySlider();
    this.setupStickyBar();
    this.shuffleRelated(); // pick the four on show before they are wired up
    this.setupRelatedCards();
  }

  disconnectedCallback() {
    if (this.relatedTeardown) this.relatedTeardown.forEach((off) => off());
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.gallery && this.onGalleryScroll) this.gallery.removeEventListener('scroll', this.onGalleryScroll);
    if (this.stickyObserver) this.stickyObserver.disconnect();
    if (this.stickyAdd && this.onStickyAdd) this.stickyAdd.removeEventListener('click', this.onStickyAdd);
    if (this.onOmnisendForms) window.removeEventListener('omnisendForms', this.onOmnisendForms);
  }

  /**
   * "You might also like" shows four of the collection, drawn fresh each visit.
   *
   * The Liquid renders every other product in the collection as a candidate and
   * the CSS keeps the first four; here we shuffle the lot, mark four as shown
   * and move them to the end of the grid so the row is in random ORDER as well
   * as a random four. The hidden candidates cost nothing: their images are lazy
   * and `display: none` keeps the browser from ever fetching them.
   *
   * This runs from a deferred module, so it lands long before anyone has
   * scrolled the length of a PDP to see this section.
   */
  shuffleRelated() {
    const grid = this.querySelector('[data-apdp-related]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[data-apdp-rcard]'));
    if (cards.length <= 4) return; // nothing to choose between

    for (let i = cards.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }

    grid.classList.add('is-shuffled');
    cards.slice(0, 4).forEach((card) => {
      card.classList.add('is-shown');
      grid.appendChild(card); // hidden cards hold no grid slot, so this orders the row
    });
  }

  /**
   * "You might also like" cards. Every shot of the product is already stacked
   * in the card by the Liquid, first one showing; here we make hovering the
   * card reveal the second shot (and drop back to the first on leave), and the
   * arrows step through the rest, wrapping at either end.
   *
   * A card with a single image is left alone — it has no arrows and nothing to
   * swap to. Without JS the first shot simply stays put.
   */
  setupRelatedCards() {
    const cards = Array.from(this.querySelectorAll('[data-apdp-rcard]'));
    if (cards.length === 0) return;

    this.relatedTeardown = [];
    // Hover only means anything with a pointer; on touch the arrows are the
    // whole interaction, and they're always visible there.
    const canHover = window.matchMedia('(hover: hover)').matches;

    cards.forEach((card) => {
      const frames = Array.from(card.querySelectorAll('.apdp__rcard-img'));
      if (frames.length < 2) return;

      let index = 0;
      const show = (next) => {
        const i = (next + frames.length) % frames.length;
        if (i === index) return;
        frames[index].classList.remove('is-active');
        frames[i].classList.add('is-active');
        index = i;
      };

      const bind = (el, type, handler) => {
        if (!el) return;
        el.addEventListener(type, handler);
        this.relatedTeardown.push(() => el.removeEventListener(type, handler));
      };

      const step = (delta) => (event) => {
        // The arrows sit over the card's link; a click on one changes the
        // image, it doesn't open the product.
        event.preventDefault();
        event.stopPropagation();
        show(index + delta);
      };

      bind(card.querySelector('[data-apdp-rcard-prev]'), 'click', step(-1));
      bind(card.querySelector('[data-apdp-rcard-next]'), 'click', step(1));

      if (canHover) {
        bind(card, 'mouseenter', () => show(1));
        bind(card, 'mouseleave', () => show(0));
      }
    });
  }

  /**
   * Mobile only: the vertical image column becomes a horizontal swipe slider
   * (CSS scroll-snap). Here we build the pagination dots and keep them in sync
   * with the swipe, and let a dot tap scroll to its image.
   */
  setupGallerySlider() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    this.gallery = this.querySelector('[data-apdp-gallery] .apdp__gallery');
    const dotsWrap = this.querySelector('[data-apdp-dots]');
    if (!this.gallery || !dotsWrap) return;

    const slides = Array.from(this.gallery.querySelectorAll('.apdp__media'));
    if (slides.length < 2) return;

    const dots = slides.map((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'apdp__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', `Image ${i + 1}`);
      dot.addEventListener('click', () => {
        this.gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
      dotsWrap.appendChild(dot);
      return dot;
    });

    // Highlight the dot for whichever slide is closest to the scroll position.
    this.onGalleryScroll = () => {
      const x = this.gallery.scrollLeft;
      let active = 0;
      let best = Infinity;
      slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft - x);
        if (dist < best) {
          best = dist;
          active = i;
        }
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
    };
    this.gallery.addEventListener('scroll', this.onGalleryScroll, { passive: true });
  }

  #parseVariants() {
    const tag = this.querySelector('[data-apdp-variants]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent);
    } catch (error) {
      console.error('Anemoia product: bad variant JSON', error);
      return [];
    }
  }

  #initSelectedFromMarkup() {
    this.querySelectorAll('[data-apdp-value].is-selected').forEach((button) => {
      this.selected[button.dataset.position] = button.dataset.value;
    });
  }

  // ----- Variant / option selection -----
  setupOptions() {
    this.valueButtons = Array.from(this.querySelectorAll('[data-apdp-value]'));
    this.onValueClick = (event) => {
      const button = event.target.closest('[data-apdp-value]');
      if (!button) return;
      const { position, value } = button.dataset;
      this.selected[position] = value;

      // Single-select within the same option group.
      this.valueButtons
        .filter((b) => b.dataset.position === position)
        .forEach((b) => b.classList.toggle('is-selected', b === button));

      this.#updateVariant();
    };
    this.addEventListener('click', this.onValueClick);
    this.#updateVariant();
  }

  /**
   * How many option groups exist, i.e. how many choices make a full variant.
   * Defensive about valueButtons: setupNotify runs before setupOptions and can
   * reach #currentVariant() through it.
   */
  #optionCount() {
    return new Set((this.valueButtons || []).map((button) => button.dataset.position)).size;
  }

  /**
   * The variant matching the current choices, or undefined while any option is
   * still unchosen. The guard matters: `[].every()` is true, so without it an
   * empty selection would match — and silently return the first variant — on a
   * page that deliberately opens with no size picked.
   */
  #currentVariant() {
    const positions = Object.keys(this.selected);
    if (positions.length < this.#optionCount()) return undefined;
    return this.variants.find((variant) =>
      positions.every((pos) => variant.options[Number(pos) - 1] === this.selected[pos])
    );
  }

  #updateVariant() {
    const variant = this.#currentVariant();

    // Grey out option values that have no available variant given the rest.
    this.#markUnavailable();

    if (!variant) {
      // Two different no-variant states: nothing picked yet (the opening state,
      // "Select size"), versus a combination that does not exist at all.
      const incomplete = Object.keys(this.selected).length < this.#optionCount();
      if (this.addBtn) this.addBtn.disabled = true;
      if (this.addLabel) {
        this.addLabel.textContent = incomplete
          ? this.dataset.selectLabel || 'Select size'
          : this.dataset.unavailableLabel || 'Unavailable';
      }
      this.#syncBuyForm(null);
      this.#syncNotify(null);
      this.#syncSticky();
      return;
    }

    if (this.addBtn) {
      this.addBtn.disabled = !variant.available;
      this.addBtn.dataset.variantId = String(variant.id);
      if (variant.image) this.addBtn.dataset.image = variant.image;
    }
    this.#syncBuyForm(variant);
    if (this.addLabel) {
      this.addLabel.textContent = variant.available
        ? this.dataset.addLabel || this.addLabel.textContent
        : 'Sold out';
    }
    if (this.priceNow) this.priceNow.innerHTML = variant.price;
    if (this.priceCompare) {
      this.priceCompare.innerHTML = variant.compare_at || '';
      this.priceCompare.style.display = variant.compare_at ? '' : 'none';
    }

    if (variant.media) this.#scrollToMedia(variant.media);

    this.#syncNotify(variant);
    this.#syncSticky();
  }

  // ----- Back in stock ("Notify me when available" -> Omnisend form) -----

  /**
   * Sold-out variants swap Add to cart for a live "Notify me when available"
   * button. It opens the Omnisend popup form through Omnisend's documented
   * `openForm` custom trigger — Omnisend owns the email field and the consent.
   * Once that form is submitted we push a custom event carrying the product,
   * variant and size, so the signup records WHAT they are waiting for.
   */
  setupNotify() {
    this.notifyBtn = this.querySelector('[data-apdp-notify]');
    this.notifyLabel = this.querySelector('[data-apdp-notify-label]');
    this.stickyNotify = this.querySelector('[data-apdp-sticky-notify]');
    this.stickyNotifyLabel = this.querySelector('[data-apdp-sticky-notify-label]');
    this.notifyFormId = (this.dataset.notifyFormId || '').trim();
    if (!this.notifyBtn && !this.stickyNotify) return;

    this.notified = readNotified();
    this.pendingNotify = null;

    this.onNotifyClick = () => this.openNotifyForm();
    this.notifyBtn?.addEventListener('click', this.onNotifyClick);
    this.stickyNotify?.addEventListener('click', this.onNotifyClick);

    // Omnisend dispatches this on its own forms (view / submit / close).
    this.onOmnisendForms = (event) => this.#onOmnisendForm(event);
    window.addEventListener('omnisendForms', this.onOmnisendForms);
  }

  openNotifyForm() {
    const variant = this.#currentVariant();
    if (!variant || !this.notifyFormId) return;

    // Held until the form is submitted (or closed), so the event we send after
    // is about the size they were actually looking at.
    this.pendingNotify = variant;

    if (!window.omnisend) {
      console.warn('Anemoia product: Omnisend snippet not found; the notify form cannot open.');
      return;
    }
    window.omnisend.push(['openForm', this.notifyFormId]);
  }

  #onOmnisendForm(event) {
    const detail = event.detail || {};
    const formId = detail.form?.id;
    // Other Omnisend forms share the page (the slot machine popup) — skip them.
    if (this.notifyFormId && formId && formId !== this.notifyFormId) return;

    if (detail.type === 'close') {
      this.pendingNotify = null;
      return;
    }
    if (detail.type !== 'submit') return;

    const variant = this.pendingNotify;
    this.pendingNotify = null;
    if (!variant) return;

    this.#trackBackInStock(variant);
    this.notified.add(String(variant.id));
    writeNotified(this.notified);
    this.#syncNotify(this.#currentVariant());
  }

  /**
   * Report the product / variant / size behind this signup to Omnisend.
   *
   * Deliberately lean: Omnisend already has the catalogue, so this only carries
   * what identifies the request (which product, which size) plus the few fields
   * worth printing straight into the restock email.
   */
  #trackBackInStock(variant) {
    const name = (this.dataset.notifyEvent || '').trim() || 'back in stock request';
    const size = this.#sizeOf(variant);
    const product = {
      id: String(this.dataset.productId || ''),
      variantId: String(variant.id),
      title: this.dataset.productTitle || '',
      size,
      // Shopify prices are minor units (13300); send 133.00 so the email reads
      // as money rather than a five-figure number.
      price: Number(((variant.price_cents || 0) / 100).toFixed(2)),
      currency: this.dataset.currency || '',
      url: this.dataset.productUrl || window.location.href,
      imageUrl: variant.image || this.dataset.productImage || '',
    };
    // Only when it says something the size doesn't — e.g. "Ash / M".
    if (variant.title && variant.title !== size) product.variantTitle = variant.title;

    // Omnisend only records events against an identified contact, and it is the
    // form submission that identifies them — give that a beat to land first.
    window.setTimeout(() => {
      window.omnisend = window.omnisend || [];
      window.omnisend.push(['track', name, { origin: 'api', properties: { product } }]);
    }, 1500);
  }

  /** The size they asked about, when the product has a size option. */
  #sizeOf(variant) {
    const position = Number(this.dataset.sizePosition || 0);
    if (position > 0 && variant.options?.[position - 1]) return variant.options[position - 1];
    return variant.title || '';
  }

  /**
   * Swap Add to cart <-> Notify me for the chosen variant (both here and on the
   * mobile sticky bar), and keep the confirmation on a size already signed up
   * for. `hidden` rather than a class so the button is out of the tab order.
   */
  #syncNotify(variant) {
    if (!this.notifyBtn && !this.stickyNotify) return;

    const show = Boolean(variant) && !variant.available && this.notifyFormId !== '';
    const done = show && this.notified.has(String(variant.id));
    const label = done
      ? this.dataset.notifyDoneLabel || "You're on the list"
      : this.dataset.notifyLabel || 'Notify me when available';

    if (this.addBtn) this.addBtn.hidden = show;
    if (this.stickyAdd) this.stickyAdd.hidden = show;

    [
      [this.notifyBtn, this.notifyLabel],
      [this.stickyNotify, this.stickyNotifyLabel],
    ].forEach(([button, labelEl]) => {
      if (!button) return;
      button.hidden = !show;
      button.disabled = done;
      if (labelEl) labelEl.textContent = label;
    });
  }

  /**
   * Point the dynamic-checkout form at the chosen variant, and hide it when
   * that variant cannot be bought — Shop Pay would otherwise open a checkout
   * that fails at the last step.
   */
  #syncBuyForm(variant) {
    if (this.buyFormVariant && variant) this.buyFormVariant.value = String(variant.id);
    if (this.buyForm) this.buyForm.classList.toggle('is-unavailable', !variant?.available);
  }

  /**
   * True while the only thing standing between the visitor and the cart is an
   * unchosen size — as opposed to a sold-out or non-existent combination, where
   * there is nothing useful to send them to.
   */
  #needsSize() {
    return !this.#currentVariant() && Object.keys(this.selected || {}).length < this.#optionCount();
  }

  /** Mirror the main CTA's variant / label / price onto the sticky bar. */
  #syncSticky() {
    if (!this.stickyAdd || !this.addBtn) return;
    /* The bar exists on mobile only (it is display:none above 749px), and there
       a dead "Select your size" button is a wall: the size row is somewhere up
       the page, and the disabled button says so without offering a way there.
       So in that one state it stays live and becomes a jump link instead. The
       main CTA is left disabled — it sits right beside the sizes already. */
    const pick = this.#needsSize();
    this.stickyAdd.disabled = pick ? false : this.addBtn.disabled;
    this.stickyAdd.classList.toggle('is-pick', pick);
    this.stickyAdd.dataset.variantId = this.addBtn.dataset.variantId || '';
    if (this.addBtn.dataset.image) this.stickyAdd.dataset.image = this.addBtn.dataset.image;
    if (this.stickyLabel && this.addLabel) this.stickyLabel.textContent = this.addLabel.textContent;
    if (this.stickyPrice && this.priceNow) this.stickyPrice.innerHTML = this.priceNow.innerHTML;
  }

  /**
   * Scroll the size row into the middle of the screen and pulse it, so the tap
   * that asked for it visibly lands somewhere. Focus follows once the scroll has
   * settled, with `preventScroll` — focusing scrolls on its own and would fight
   * the smooth animation halfway through it.
   */
  #jumpToSize() {
    const option = this.querySelector('[data-apdp-option]');
    if (!option) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    option.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });

    option.classList.remove('is-calling');
    void option.offsetWidth; // restart the pulse on a second tap
    option.classList.add('is-calling');
    window.setTimeout(() => option.classList.remove('is-calling'), 1400);

    const chip = option.querySelector('.apdp__value:not(.is-unavailable)') || option.querySelector('.apdp__value');
    window.setTimeout(() => chip?.focus?.({ preventScroll: true }), still ? 0 : 450);
  }

  /**
   * A slim Add to cart bar pinned to the bottom, shown only while the main CTA
   * is scrolled out of view (and hidden again once it's back on screen, or once
   * the footer is reached — the bar never covers the footer links).
   */
  setupStickyBar() {
    this.sticky = this.querySelector('[data-apdp-sticky]');
    this.stickyAdd = this.querySelector('[data-apdp-sticky-add]');
    this.stickyLabel = this.querySelector('[data-apdp-sticky-label]');
    this.stickyPrice = this.querySelector('[data-apdp-sticky-price]');
    if (!this.sticky || !this.addBtn) return;

    // The sticky button is a proxy for the real CTA; fly from the sticky button.
    // Except while a size is still unchosen, where it takes them to the sizes.
    this.onStickyAdd = (event) => {
      event.preventDefault();
      if (this.#needsSize()) return this.#jumpToSize();
      this.addToCart(this.stickyAdd);
    };
    this.stickyAdd?.addEventListener('click', this.onStickyAdd);

    if ('IntersectionObserver' in window) {
      // The wrapper, not the button: a sold-out variant hides Add to cart, and a
      // `hidden` element has no box, which would read as "scrolled away" and pin
      // the bar open at the top of the page.
      const cta = this.querySelector('[data-apdp-cta]') || this.addBtn;
      // The footer is the second reason to stay down: a bar pinned to the bottom
      // would sit on top of the footer links and make them unreadable/untappable
      // exactly where the page ends.
      const footer = this.querySelector('.apdp__footer');
      this.ctaOnScreen = true;
      this.footerOnScreen = false;

      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target === footer) this.footerOnScreen = entry.isIntersecting;
            else this.ctaOnScreen = entry.isIntersecting;
          });
          this.sticky.classList.toggle('is-visible', !this.ctaOnScreen && !this.footerOnScreen);
        },
        { threshold: 0 }
      );

      this.stickyObserver.observe(cta);
      if (footer) this.stickyObserver.observe(footer);
    }

    this.#syncSticky();
  }

  /**
   * Disable values that can't form an available variant with current picks, and
   * flag the ones down to their last few units so the chip can show its low
   * stock dot. "Low" only counts when Shopify is actually tracking every variant
   * behind that value (inventory null = untracked, so we say nothing).
   */
  #markUnavailable() {
    const threshold = Number(this.dataset.lowStock || 0);

    this.valueButtons.forEach((button) => {
      const pos = button.dataset.position;
      const trial = { ...this.selected, [pos]: button.dataset.value };
      const positions = Object.keys(trial);
      const matches = this.variants.filter((variant) =>
        positions.every((p) => variant.options[Number(p) - 1] === trial[p])
      );
      const buyable = matches.filter((variant) => variant.available);
      button.classList.toggle('is-unavailable', buyable.length === 0);

      const counts = buyable.map((variant) => variant.inventory).filter((n) => typeof n === 'number');
      const left = counts.length > 0 ? Math.min(...counts) : 0;
      const low =
        threshold > 0 &&
        buyable.length > 0 &&
        counts.length === buyable.length && // every one of them tracked
        left > 0 && // continue-selling can run negative; that isn't "low"
        left <= threshold;
      button.classList.toggle('is-low', low);
    });
  }

  #scrollToMedia(mediaId) {
    const figure = this.querySelector(`[data-apdp-media-id="${mediaId}"]`);
    if (figure && !reducedMotion.matches) {
      figure.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ----- Add to cart -----
  setupAddToCart() {
    if (!this.addBtn) return;
    this.adding = false;
    this.onAddClick = (event) => {
      event.preventDefault();
      this.addToCart();
    };
    this.addBtn.addEventListener('click', this.onAddClick);
  }

  #flyToCart(sourceBtn = this.addBtn) {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = sourceBtn.dataset.image || this.addBtn.dataset.image;
    if (!cartIcon || !image || reducedMotion.matches) return;
    const fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--main');
    fly.style.setProperty('background-image', `url(${image})`);
    fly.style.setProperty('--start-opacity', '0');
    fly.source = sourceBtn;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  async addToCart(sourceBtn = this.addBtn) {
    const variantId = this.addBtn.dataset.variantId;
    if (!variantId || this.addBtn.disabled || this.adding) return;
    this.adding = true;

    // Shared CTA feedback: collapse the label, swell + pop the cart image.
    sourceBtn.classList.add('is-adding');
    window.setTimeout(() => sourceBtn.classList.remove('is-adding'), 900);
    this.#flyToCart(sourceBtn);

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (data?.status) return;

      document.dispatchEvent(
        new CartAddEvent(data ?? undefined, 'anemoia-product', {
          source: 'product-form-component',
          itemCount: 1,
          variantId: String(variantId),
          sections: data?.sections,
        })
      );

      openCartDrawer(); // pop the side cart open on add
    } catch (error) {
      console.error('Anemoia product add to cart failed', error);
    } finally {
      this.adding = false;
    }
  }

  // ----- Size guide dialog -----
  setupSizeGuide() {
    this.dialog = this.querySelector('[data-apdp-sizeguide]');
    if (!this.dialog) return;
    this.querySelectorAll('[data-apdp-sizeguide-open]').forEach((btn) =>
      btn.addEventListener('click', () => this.dialog.showModal())
    );
    this.querySelector('[data-apdp-sizeguide-close]')?.addEventListener('click', () => this.dialog.close());
    // Click on the backdrop closes it.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  // ----- Scroll parallax on gallery images -----
  setupParallax() {
    if (reducedMotion.matches) return;
    this.media = Array.from(this.querySelectorAll('[data-apdp-media-id]'));
    if (this.media.length === 0) return;

    this.ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      this.media.forEach((figure) => {
        const img = figure.querySelector('.apdp__img');
        if (!img) return;
        const rect = figure.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return; // offscreen, skip
        const progress = clamp((rect.top + rect.height / 2) / vh, 0, 1);
        const speed = figure.dataset.col === '1' ? 1.6 : 1;
        const py = -8 + (progress - 0.5) * 10 * speed;
        img.style.setProperty('--apdp-py', `${py.toFixed(2)}%`);
      });
      this.ticking = false;
    };

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(update);
    };
    this.onResize = this.onScroll;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    update();
  }
}

if (!customElements.get('anemoia-product')) {
  customElements.define('anemoia-product', AnemoiaProduct);
}
